import React, { Component } from 'react';
import moment from 'moment';
import lodashCompact from 'lodash/fp/compact';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import pluralize from 'pluralize';
import { useMediaQuery } from 'react-responsive';

import {
  LISTING_STATUS_ACTIVE,
  LISTING_IMAGE_PLACEHOLDER_URL_LARGE,
  LISTING_STATUS_ARCHIVED,
  LISTING_IMAGE_ARCHIVED_PLACEHOLDER_URL_LARGE,
} from 'common/listingConstants';

import {
  SUNLOCK_STATUS_ACTIVE,
  SUNLOCK_STATUS_UNREACHABLE,
} from 'common/sunlocks/sunlockConstants';

import {
  getListingUrlPath,
  getListingManagedPhotoUrls,
  isListingTenantScreening,
  isListingSunRise,
  getListingCityStateString,
  getAppliancesPhotos,
  getMarketingItemsPhotos,
  isSelfShowingListing,
  shouldHideSunlockCodes,
} from 'common/listingHelpers';

import { getPreLeaseDaysOnMarket } from 'common/preLeaseHelpers';

import { formatPrice } from 'common/formatters';

import { hasRoleAdmin, hasRoleAnonymous } from 'common/userRoles';

import { MEDIA_QUERY_SMALL_SCREEN } from 'client/constants/mediaQueries';

import branch from 'client/core/branch';

import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrFlex from 'client/components/SrFlex';
import SrLink from 'client/components/SrLink/SrLink';
import SrPartnerActionButton from 'client/components/SrPartnerActionButton/SrPartnerActionButton';
import SrPhotosModal from 'client/components/SrPhotosModal/SrPhotosModal';

const LocalListingHeaderContent = ({
  listing,
  marketingPartnerListing,
  isCurrentPartner,
  isAdmin,
  isAnonymous,
  currentPartner,
  onOpenShareListingClick,
  onOpenListingLinksClick,
  onOpenSupportLinksClick,
  onDOMMedianClick,
  isLandlord,
  isTenantScreening,
  isSunRise,
  currentUser,
}) => {
  const _getDaysAgoCompletedText = (setupCompletedDate) => {
    const daysAgoCompleted = moment().diff(setupCompletedDate, 'days');
    return `Setup completed ${daysAgoCompleted} ${pluralize('day', daysAgoCompleted)} ago`;
  };

  const _getSunriseListingDetail = () => {
    const setupCompletedDate = marketingPartnerListing?.setupCompletedDate;

    if (setupCompletedDate != null) {
      const completedText = _getDaysAgoCompletedText(setupCompletedDate);

      return (
        <SrText color="green" weight="800" font="greycliff" fontSize="16px" lineHeight="19px">
          {completedText}
        </SrText>
      );
    }
    return (
      <SrText color="yellowGold" weight="800" font="greycliff" fontSize="16px" lineHeight="19px">
        Setup in Progress
      </SrText>
    );
  };

  const _getPreLeaseDaysOnMarket = (listing) => {
    if (!listing.preLease) {
      return {
        daysOnPreLease: null,
        daysSinceMoveIn: null,
      };
    }

    if (!listing.preLease?.completedDate) {
      return {
        daysOnPreLease: listing.daysOnMarket,
        daysSinceMoveIn: null,
      };
    }

    const daysOnPreLease = getPreLeaseDaysOnMarket({
      listing: listing,
      preLease: listing.preLease,
    });

    return {
      daysOnPreLease: daysOnPreLease,
      daysSinceMoveIn: listing.daysOnMarket - daysOnPreLease,
    };
  };

  const _getPreLeaseDOMText = (listing) => {
    const { daysOnPreLease } = _getPreLeaseDaysOnMarket(listing);

    if (!listing.preLease) {
      return null;
    }
    if (listing.preLease?.completedDate) {
      return `${pluralize('day', daysOnPreLease, true)} pre-leased`;
    }
    if (daysOnPreLease === 0 && moment().isSame(listing.preLease?.createdDate, 'day')) {
      return 'Pre-leasing started today';
    }
    return `${pluralize('day', daysOnPreLease, true)} on pre-lease`;
  };

  const isSmallScreen = useMediaQuery({
    query: MEDIA_QUERY_SMALL_SCREEN,
  });

  const renterViewButton =
    !isTenantScreening &&
    !isSunRise &&
    (listing.isSyndicated ? (
      <SrPartnerActionButton
        label="Renter View"
        iconUrl="/images/eye-blue.svg"
        onClick={onOpenListingLinksClick}
      />
    ) : (
      <SrLink target="_blank" to={getListingUrlPath(listing)}>
        <SrPartnerActionButton label="Renter View" iconUrl="/images/eye-blue.svg" />
      </SrLink>
    ));

  const shareButton = !isTenantScreening && !isSunRise && (
    <SrPartnerActionButton
      label="Share"
      iconUrl="/images/arrow-right-blue.svg"
      onClick={onOpenShareListingClick}
    />
  );

  const supportButton = !isSunRise && (
    <SrPartnerActionButton
      label="Support"
      iconUrl="/images/question-mark-blue.svg"
      iconSize="15px"
      onClick={onOpenSupportLinksClick}
    />
  );

  const sunRiseListingDetail = isSunRise && _getSunriseListingDetail();

  const buttonWidth =
    !isTenantScreening && !isSunRise ? (isSmallScreen ? '100%' : '380px') : '210px';

  const preLeaseDOMText = _getPreLeaseDOMText(listing);
  const listingCityState = getListingCityStateString(listing);
  const { daysSinceMoveIn } = _getPreLeaseDaysOnMarket(listing);

  return (
    <>
      <SrFlex wrap="wrap" justify="between">
        <SrBox>
          <span className="listingAddressContainer">
            <span className="listingAddress">
              <SrText
                font="greycliff"
                weight="800"
                fontSize={isSmallScreen ? '21px' : '28px'}
                lineHeight={isSmallScreen ? '25px' : '28px'}
                color="blueGreycliff"
              >
                {listing.addressStreetDisplay}
              </SrText>
            </span>
            <span className="listingCityState">
              <SrText font="greycliff" color="grey">
                {listingCityState}
              </SrText>
            </span>
          </span>
          <div className="listingFooter">
            <div className="listingDetails">
              {isSunRise ? (
                sunRiseListingDetail
              ) : (
                <span>
                  <SrText color="green" weight="800" font="greycliff" lineHeight="19px">
                    {`${formatPrice(listing.price)} Rent`}

                    {listing.underwrittenRent && (
                      <SrText color="green" weight="600" font="greycliff" paddingLeft="6px">
                        {formatPrice(listing.underwrittenRent)} underwritten
                      </SrText>
                    )}
                  </SrText>
                  <SrFlex>
                    {listing.daysOnMarket > 0 && (
                      <SrText font="greycliff" color="blueGreycliff" weight="600">
                        {`${listing.daysOnMarket} total ` +
                          `${pluralize('day', listing.daysOnMarket, false)} on market`}
                      </SrText>
                    )}
                    {listing.comparativeMarketAnalysisMedianDaysOnMarket > 0 && (
                      <SrText
                        font="greycliff"
                        color="blue300"
                        weight="700"
                        cursor="pointer"
                        paddingLeft="6px"
                        onClick={onDOMMedianClick}
                      >
                        (vs {listing.comparativeMarketAnalysisMedianDaysOnMarket} DOM market median)
                      </SrText>
                    )}
                  </SrFlex>
                  {listing.preLease && (
                    <SrFlex direction="col">
                      <SrBox>
                        <SrText
                          weight="600"
                          font="greycliff"
                          fontSize="14px"
                          lineHeight="19px"
                          color="grey"
                        >
                          {preLeaseDOMText}
                        </SrText>
                      </SrBox>
                      {typeof daysSinceMoveIn === 'number' && (
                        <SrBox>
                          <SrText
                            weight="600"
                            font="greycliff"
                            fontSize="14px"
                            lineHeight="19px"
                            color="grey"
                          >
                            {daysSinceMoveIn > 0
                              ? `${pluralize('days', daysSinceMoveIn, true)} since move-in ready`
                              : 'Move-in ready'}
                          </SrText>
                        </SrBox>
                      )}
                    </SrFlex>
                  )}
                  {isSelfShowingListing(listing) && (
                    <SrBox marginTop="2px">
                      <SrText
                        color="yellowGold"
                        weight="800"
                        font="greycliff"
                        fontSize="16px"
                        lineHeight="19px"
                      >
                        {listing.codeboxLock ? 'CodeBox Installed' : 'Sunlock Installed'}
                      </SrText>
                      {listing.sunlock &&
                        listing.sunlock.partnerLockCode &&
                        !shouldHideSunlockCodes(listing, currentUser) && (
                          <SrText
                            font="greycliff"
                            color="blueGreycliff"
                            opacity="0.5"
                            fontSize="16px"
                            lineHeight="19px"
                            weight="600"
                          >
                            {` (access code is ${listing.sunlock.partnerLockCode})`}
                          </SrText>
                        )}
                      {listing.codeboxLock?.masterCode &&
                        !shouldHideSunlockCodes(listing, currentUser) && (
                          <SrText
                            font="greycliff"
                            color="blueGreycliff"
                            opacity="0.5"
                            fontSize="16px"
                            lineHeight="19px"
                            weight="600"
                          >
                            {` (master code is ${listing.codeboxLock.masterCode})`}
                          </SrText>
                        )}
                    </SrBox>
                  )}
                </span>
              )}
            </div>
          </div>
        </SrBox>
        <SrBox margin="0 7px 0 3px" width={buttonWidth}>
          <SrFlex
            className={classNames('actionContainer', {
              sunrise: isSunRise,
            })}
          >
            {isAdmin && (
              <SrPartnerActionButton
                label="Job Orders"
                iconUrl="/images/id-verification-timer.svg"
                to={`/sunsetup/listing-orders/${listing.id}`}
              />
            )}
            {(isCurrentPartner || isAdmin || isLandlord) && (
              <SrPartnerActionButton
                label="Edit Listing"
                iconUrl="/images/pencil-icon-blue.svg"
                to={`/partner/${currentPartner.slug}/listing/${listing.id}/edit`}
              />
            )}
            {shareButton}
            {renterViewButton}
            {isAnonymous ? null : supportButton}
          </SrFlex>
        </SrBox>
      </SrFlex>
    </>
  );
};

@branch({
  appUrl: ['env', 'APP_URL'],
  userAgent: ['analytics', 'userAgent'],
})
class SrPartnerListingHeader extends Component {
  static propTypes = {
    listing: PropTypes.object.isRequired,
    marketingPartnerListing: PropTypes.object,
    currentPartnerUser: PropTypes.object,
    currentPartner: PropTypes.object.isRequired,
    currentUser: PropTypes.object.isRequired,
    onOpenListingLinksClick: PropTypes.func.isRequired,
    onOpenSupportLinksClick: PropTypes.func.isRequired,
    onOpenShareListingClick: PropTypes.func.isRequired,
    appUrl: PropTypes.string.isRequired,
    userAgent: PropTypes.object.isRequired,
    isLandlord: PropTypes.bool,
    dispatch: PropTypes.func.isRequired,
    onLowerRentPrice: PropTypes.func.isRequired,
    onDOMMedianClick: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);

    this.state = {
      showListingPhotosModal: false,
    };
  }

  _onClickPhotosHeader = () => {
    this.setState({
      showListingPhotosModal: !this.state.showListingPhotosModal,
    });
  };

  render() {
    const {
      listing,
      marketingPartnerListing,
      onOpenListingLinksClick,
      onOpenSupportLinksClick,
      onOpenShareListingClick,
      onDOMMedianClick,
      isTransitioning,
      currentPartnerUser,
      currentPartner,
      currentUser,
      appUrl,
      userAgent,
      isLandlord,
      dispatch,
      onLowerRentPrice,
    } = this.props;

    const { showListingPhotosModal } = this.state;

    const isSunlockInstalled =
      listing.sunlock &&
      [SUNLOCK_STATUS_ACTIVE, SUNLOCK_STATUS_UNREACHABLE].includes(listing.sunlock.status);
    const isAdmin = hasRoleAdmin(currentUser.roles);
    const isAnonymous = hasRoleAnonymous(currentUser.roles);
    const isListingActive = listing.status === LISTING_STATUS_ACTIVE;
    const isCurrentPartner =
      currentPartnerUser && currentPartnerUser.partnerId === currentPartner.id;
    const isTenantScreening = isListingTenantScreening(listing);
    const isSunRise = isListingSunRise(listing);
    const sunroomPhotoUrls = getListingManagedPhotoUrls({
      listing,
      useWatermarks: false,
    });
    let [heroPhotoUrl] = sunroomPhotoUrls;
    const appliancesPhotos = getAppliancesPhotos(listing);
    const marketingItemsPhotos = getMarketingItemsPhotos(listing);
    const allPhotos = lodashCompact([
      ...sunroomPhotoUrls,
      ...appliancesPhotos,
      ...marketingItemsPhotos,
    ]);

    if (!heroPhotoUrl || isTenantScreening) {
      heroPhotoUrl = `${appUrl}${LISTING_IMAGE_PLACEHOLDER_URL_LARGE}`;

      if (isTenantScreening && listing.status === LISTING_STATUS_ARCHIVED) {
        heroPhotoUrl = `${appUrl}${LISTING_IMAGE_ARCHIVED_PLACEHOLDER_URL_LARGE}`;
      }
    }

    return (
      <div className="SrPartnerListingHeader">
        <SrBox position="relative" zIndex="5">
          <div
            className={classNames(
              'listingBackgroundImage',
              allPhotos.length && 'clickableListingBackgroundImage',
            )}
            style={
              isTransitioning || !heroPhotoUrl ? {} : { backgroundImage: `url("${heroPhotoUrl}")` }
            }
            onClick={allPhotos.length ? this._onClickPhotosHeader : null}
          >
            {allPhotos.length > 0 && (
              <div className="partnerPhotoCountContainer">
                <span id="partnerPhotoCount" onClick={this._onClickPhotosHeader}>
                  See all {allPhotos.length} photos
                </span>
              </div>
            )}
          </div>
          <div
            className="listingBackgroundImageShadow"
            style={
              !heroPhotoUrl
                ? {}
                : {
                    backgroundImage: `linear-gradient(0deg, rgba(52, 44, 25, 0.35), rgba(52, 44, 25, 0.35)),
                url(${heroPhotoUrl})`,
                  }
            }
          />
        </SrBox>
        <div className="SrPartnerPageContent">
          <div className="headerContainer">
            <LocalListingHeaderContent
              listing={listing}
              marketingPartnerListing={marketingPartnerListing}
              isListingActive={isListingActive}
              isSunlockInstalled={isSunlockInstalled}
              isCurrentPartner={isCurrentPartner}
              isAdmin={isAdmin}
              isAnonymous={isAnonymous}
              currentPartner={currentPartner}
              onOpenShareListingClick={onOpenShareListingClick}
              onOpenListingLinksClick={onOpenListingLinksClick}
              onOpenSupportLinksClick={onOpenSupportLinksClick}
              onDOMMedianClick={onDOMMedianClick}
              appUrl={appUrl}
              userAgent={userAgent}
              isLandlord={isLandlord}
              dispatch={dispatch}
              onLowerRentPrice={onLowerRentPrice}
              isTenantScreening={isTenantScreening}
              isSunRise={isSunRise}
              currentUser={currentUser}
            />
          </div>
        </div>
        {showListingPhotosModal && (
          <SrPhotosModal
            photos={allPhotos}
            listing={listing}
            partner={currentPartner}
            onClose={this._onClickPhotosHeader}
          />
        )}
      </div>
    );
  }
}

SrPartnerListingHeader.displayName = 'SrPartnerListingHeader';

export default SrPartnerListingHeader;
